import { type ComponentProps, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { ReactSelectInput } from '@hivebrite/design-system/Inputs';

import { usePromoCodesDiscount } from 'hb-react/backoffice/apps/PromoCode/hooks/usePromoCodesDiscount';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { PER_PAGE } from 'hb-react/shared/components/ReactSelect/constants';
import { useAdaptiveReactSelect } from 'hb-react/shared/components/ReactSelect/hooks/useAdaptiveReactSelect';
import { type QueryFn } from 'hb-react/shared/components/ReactSelect/ReactSelectAdaptive/types';
import {
  type PromoCodeCamelCase,
  type PromoCodeOption,
  type PromoCodesResponse,
} from 'hb-react/shared/components/ReactSelect/ReactSelectPromoCode/types';
import { axiosWithSyntaxHandlers } from 'hb-react/shared/utils/axios';
import { type Currency } from 'hb-react/types/network';

const extractData = (response: PromoCodesResponse) => response.promoCodes;

export const promoCodeToOptions = (
  promoCodes: Array<PromoCodeCamelCase>,
): Array<PromoCodeOption> =>
  promoCodes.map((promoCode) => ({
    ...promoCode,
    value: promoCode.id,
  }));

const shouldUseAsync = (defaultOptionsQueryResponse: PromoCodesResponse) =>
  defaultOptionsQueryResponse.totalItems >
  defaultOptionsQueryResponse.promoCodes.length;

const ReactSelectPromoCode = ({
  id,
  name,
  currency,
  price,
  infoMessage,
  menuPosition,
  showDiscountMessage = true,
  isClearable = true,
  excludedIds = [],
  ...props
}: ComponentProps<typeof ReactSelectInput> & {
  currency: Currency;
  excludedIds?: Array<number>;
  infoMessage?: string;
  menuPosition?: 'absolute' | 'fixed';
  price: number;
  showDiscountMessage?: boolean;
}) => {
  const { currentNetwork } = useDefaultAppContexts();
  const networkId = currentNetwork.id;

  const queryFn = useCallback<QueryFn<PromoCodesResponse>>(
    ({ searchText, ids }, signal) =>
      axiosWithSyntaxHandlers
        .get(Routes.backoffice_network_promo_codes_path(networkId!), {
          params: {
            fulltext: searchText,
            format: 'json',
            active_only: true,
            per_page: ids ? ids.length : PER_PAGE,
            ids,
          },
          signal,
        })
        .then((res) => res.data),
    [networkId],
  );

  const queryKey = useMemo(() => [networkId, 'promoCodes'], [networkId]);

  const {
    defaultOptions,
    isAsync,
    isLoading,
    loadOptions,
    value: valueToUse,
  } = useAdaptiveReactSelect({
    queryKey,
    queryFn,
    extractData,
    shouldUseAsync,
    dataToOptions: promoCodeToOptions,
    isMulti: false,
    value: props.value,
  });

  // Filter out excluded promo codes from options
  const filteredDefaultOptions = useMemo(
    () =>
      excludedIds.length > 0
        ? defaultOptions.filter((option) => !excludedIds.includes(option.value))
        : defaultOptions,
    [defaultOptions, excludedIds],
  );

  // Create a filtered load options function for async mode
  const filteredLoadOptions = useCallback(
    async (inputValue: string) => {
      if (!loadOptions) {
        return [];
      }
      const options = await loadOptions(inputValue);
      return excludedIds.length > 0
        ? options.filter((option) => !excludedIds.includes(option.value))
        : options;
    },
    [loadOptions, excludedIds],
  );

  const { discount } = usePromoCodesDiscount({
    selectedPromoCodes: valueToUse,
    price,
    currency,
    multiplePromoCodesEnabled: false,
  });

  const { valueOff, newPrice } = discount;

  const translations = {
    placeholder: s__('PromoCode|Select a promo code'),
    ariaLabel: s__('PromoCode|Associated promo code'),
    discountMessage: s__(
      'PromoCode|This promo code gives %{valueOff}% off, making the price %{newPrice}',
    ).i__({
      valueOff,
      newPrice,
    }),
  };

  return (
    <Box flexDirection="column" gridRowGap={2} flex={1}>
      {infoMessage && <Metadata mb={4}>{infoMessage}</Metadata>}
      <ReactSelectInput
        {...props}
        id={id}
        name={name}
        placeholder={translations.placeholder}
        defaultOptions={filteredDefaultOptions}
        ariaLabel={translations.ariaLabel}
        isMulti={false}
        isClearable={isClearable}
        menuPosition={menuPosition}
        isAsync={isAsync}
        cacheOptions={false}
        options={isAsync ? undefined : filteredDefaultOptions}
        isLoading={isLoading}
        loadOptions={filteredLoadOptions}
        value={valueToUse}
      />
      {showDiscountMessage && (
        <Metadata mb={1}>
          {discount.valueOff !== null ? translations.discountMessage : '\u00A0'}
        </Metadata>
      )}
    </Box>
  );
};

export default ReactSelectPromoCode;

const Metadata = styled(Box).attrs(({ theme }) => ({
  as: 'span',
  display: 'inline-block',
  font: 'metadata-regular-dove',
  fontWeight: theme.fontWeights[0],
}))``;
